import React, { Component } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Icon,
  Button,
  Table,
  Tag,
  Badge,
  Popconfirm,
  message
} from 'antd'
import './DeskMonitoring.scss'

import { TableCol } from 'components/TableCol'
import moment from 'moment'

import { PermissionService } from 'services/PermissionService'
import { Permissions } from '../../../permissions'
import { BaseRequest } from 'models/request/BaseRequest'
import { TicketRequest } from 'models/request/TicketRequest'
import { TenantService } from 'connection/TenantService'
import { ConfigurationRequest } from 'models/request/ConfigurationRequest'
import { getAgents, getCaller, changeTicketStatus, getTicketsMetrics, getOpenTickets, getAgentsMetrics, getWaitingTickets } from 'services/CeberusApiService'
import { AgentRequest } from 'models/request/AgentRequest'

export class DeskMonitoring extends Component<BaseRequest> {
  state = {
    isLoading: false,
    ticketsMetrics: {
      waiting: null,
      open: null,
      transferred: null,
      closedAttendant: null,
      missed: null,
      closedClient: null
    },
    agentsMetrics: [],
    waitingTickets: [],
    openTickets: [],
    canEdit: PermissionService.hasPermission(Permissions.Application_Desk__Edit)
  }

  componentDidMount() {
    this.getMetrics()
  }

  agentsColumns = [
    TableCol({
      title: 'Identity',
      sort: (a, b) => a.identity.localeCompare(b.identity),
      filter: (value, record) => record.identity.includes(value)
    }),
    TableCol({
      title: 'Status',
      sort: (a, b) => a.status.localeCompare(b.status),
      filter: (value, record) =>
        record.status.toLowerCase() === value.toLowerCase(),
      render: status => (
        <span>
          <Badge
            status={status === 'Online'
              ? 'success'
              : status === 'Offline'
                ? 'error'
                : status === 'Invisible'
                  ? 'default'
                  : 'warning'}
            text={status}
          />
        </span>
      ),
      filters: [
        { text: 'Invisible', value: 'Invisible' },
        { text: 'Online', value: 'Online' },
        { text: 'Offline', value: 'Offline' },
        { text: 'Pause', value: 'Pause' }
      ],
      dataIndex: null,
      ellipsis: null,
      key: null,
      width: null
    }),
    TableCol({
      title: 'Opened Tickets',
      sort: (a, b) => a.openedTickets - b.openedTickets
    }),
    TableCol({
      title: 'Teams',
      filter: (value, record) => record.teams.includes(value),
      render: tags => (
        <span>
          {tags.map(tag => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </span>
      )
    }),
    TableCol({
      title: 'Agent Slots',
      sort: (a, b) => a.agentSlots - b.agentSlots
    })
  ]

  ticketsColumns = [
    TableCol({
      title: 'Id',
      sort: (a, b) => a.id.localeCompare(b.id),
      filter: (value, record) => record.id.includes(value)
    }),
    TableCol({
      title: 'Sequential Id',
      sort: (a, b) => a.sequentialId - b.sequentialId
    }),
    TableCol({
      title: 'Queue Time',
      sort: (a, b) => moment(a.queueTime, 'HH:mm:ss').diff(moment(b.queueTime, 'HH:mm:ss'))
    }),
    TableCol({
      title: 'Agent Identity',
      sort: (a, b) => a.agentIdentity.localeCompare(b.agentIdentity)
    }),
    TableCol({
      title: 'Team',
      sort: (a, b) => a.team.localeCompare(b.team),
      filter: (value, record) => record.team === value
    }),
    TableCol({
      title: 'Customer Identity',
      sort: (a, b) => a.customerIdentity.localeCompare(b.customerIdentity)
    }),
    TableCol({
      title: 'Actions',
      sort: (a, b) => a.customerIdentity.localeCompare(b.customerIdentity),
      render: (_, record) => (
        <div>
          {this.state.canEdit && (
            <Popconfirm
              title="Are you sure close this ticket?"
              onConfirm={() => this.closeTicket(record)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip placement="top" title={'Close'}>
                <Button
                  type="link"
                  shape="circle"
                  icon="check-circle"
                  loading={record.isLoading}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      )
    })
  ]

  async closeTicket(ticket) {
    try {
      ticket.isLoading = true
      const ticketRequest = new TicketRequest()
      ticketRequest.BotIdentifier = this.props.BotIdentifier
      ticketRequest.ClusterName = TenantService.getSelectedTenant().name
      ticketRequest.Ticket = {
        Id: ticket.id,
        Status: 'ClosedAttendant'
      }
      await changeTicketStatus(ticketRequest)
      message.success('Ticket was closed successfully')
      await this.getMetrics()
    } catch (ex) {
      console.error(ex)
      message.error(
        'There were a problem closing the ticket, check you console for more information!'
      )
    }
  }

  async getMetrics() {
    this.setState({ isLoading: true })
    try {
      const ticketsMetricsPromise = this.getTicketsMetrics()
      const agentsMetricsPromise = this.getAgentsMetrics()
      const waitingTicketsPromise = this.getWaitingTickets()
      await Promise.all([
        ticketsMetricsPromise,
        agentsMetricsPromise,
        waitingTicketsPromise
      ])
    } catch (e) {
      console.error(e)
    } finally {
      this.setState({ isLoading: false })
    }
  }

  async getTicketsMetrics() {
    const ticketRequest = new TicketRequest()
    ticketRequest.BotIdentifier = this.props.BotIdentifier
    ticketRequest.ClusterName = TenantService.getSelectedTenant().name
    const tickets = await getTicketsMetrics(ticketRequest)
    this.setState({ ticketsMetrics: tickets })
  }

  async getAgentsMetrics() {
    let openTickets = []
    try {
      const ticketRequest = new TicketRequest()
      ticketRequest.BotIdentifier = this.props.BotIdentifier
      ticketRequest.ClusterName = TenantService.getSelectedTenant().name
      const openTicketsPromise = getOpenTickets(ticketRequest)
      const { openTickets: items }  = await openTicketsPromise
      openTickets = items.map(item => {
        return {
          ...item,
          key: item.id
        }
      })
      this.setState({ openTickets })
    } catch (e) {
      console.error(e)
    }
    try {
      const agentRequest = new AgentRequest()
      agentRequest.BotIdentifier = this.props.BotIdentifier
      agentRequest.ClusterName = TenantService.getSelectedTenant().name
      const agentsMetricsPromise = getAgentsMetrics(agentRequest)
      
      const request = new AgentRequest()
      request.ClusterName = TenantService.getSelectedTenant().name
      request.BotIdentifier = this.props.BotIdentifier
      const agentsPromise = getAgents(request)
      
      const configurationRequest = new ConfigurationRequest()
      configurationRequest.ClusterName = TenantService.getSelectedTenant().name
      configurationRequest.BotIdentifier = this.props.BotIdentifier
      configurationRequest.Caller = this.props.BotIdentifier
      configurationRequest.Owner = 'postmaster@desk.msging.net'
      const ownerConfigurationsPromise = getCaller(configurationRequest)

      await Promise.all([
        agentsPromise,
        agentsMetricsPromise,
        ownerConfigurationsPromise
      ])

      const { items: agents } = await agentsPromise
      let { items: agentsMetrics } = await agentsMetricsPromise
      const { items: AgentSlots } = await ownerConfigurationsPromise

      agentsMetrics = agents.map((agent, index) => {
        const agentMetric = agentsMetrics.find(
          a => a.identity === agent.identity
        )
        return {
          key: index,
          identity: decodeURIComponent(agent.identity.split('@')[0]),
          status: agentMetric.status,
          openedTickets: agentMetric.openedTickets,
          teams: agent.teams,
          agentSlots:
            (AgentSlots && AgentSlots > 0 && agent.agentSlots != undefined
              ? agent.agentSlots
              : AgentSlots) || '-',
          tickets: openTickets.filter(
            ticket => ticket.agentIdentity === agent.identity
          )
        }
      })
      this.setState({ agentsMetrics })
    } catch (e) {
      console.error(e)
    }
  }

  async getWaitingTickets() {
    const ticketRequest = new TicketRequest()
    ticketRequest.BotIdentifier = this.props.BotIdentifier
    ticketRequest.ClusterName = TenantService.getSelectedTenant().name
    let { items : tickets } = await getWaitingTickets(ticketRequest)
    tickets = tickets.map(item => {
      return {
        ...item,
        key: item.id
      }
    })
    this.setState({ waitingTickets: tickets })
  }

  render() {
    return (
      <div>
        <Card>
          <div className="flex flex-column">
            <Button
              className="self-end mb3"
              type="primary"
              icon="reload"
              onClick={() => this.getMetrics()}
              loading={this.state.isLoading}
            >
              Refresh
            </Button>
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="Waiting Tickets"
                  value={this.state.ticketsMetrics.waiting}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Open Tickets"
                  value={this.state.ticketsMetrics.open}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Transferred Tickets"
                  value={this.state.ticketsMetrics.transferred}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Closed Agent Tickets"
                  value={this.state.ticketsMetrics.closedAttendant}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Missed Tickets"
                  value={this.state.ticketsMetrics.missed}
                  prefix={<Tooltip
                    placement="top"
                    title="Tickets canceled by the user before being assigned"
                  >
                    <Icon type="info-circle" style={{ fontSize: '16px' }} />
                  </Tooltip>}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Dropped Tickets"
                  value={this.state.ticketsMetrics.closedClient}
                  prefix={<Tooltip
                    placement="top"
                    title="Tickets canceled by the user after being assigned"
                  >
                    <Icon type="info-circle" style={{ fontSize: '16px' }} />
                  </Tooltip>}
                />
              </Col>
            </Row>
          </div>
        </Card>

        <Card className="mt2">
          <Table
            title={() => 'Agents'}
            dataSource={this.state.agentsMetrics}
            columns={this.agentsColumns}
            loading={this.state.isLoading}
            rowClassName={record => record.tickets.length === 0 ? 'no-expand-row' : ''}
            expandedRowRender={rowIndex => {
              const columns = [
                TableCol({ 
                  title: 'Ticket Id'
                 }),
                TableCol({ 
                  title: 'Sequential Id'
                }),
                TableCol({ 
                  title: 'Customer Identity'
                 }),
                TableCol({ 
                  title: 'Team'
                })
              ]
              const data = rowIndex.tickets.map(ticket => ({
                key: ticket.sequentialId,
                sequentialId: ticket.sequentialId,
                ticketId: ticket.id,
                customerIdentity: ticket.customerIdentity,
                team: ticket.team
              }))
              return (
                <Table dataSource={data} columns={columns} pagination={false} />
              )
            }}
          />
        </Card>

        <Card className="mt2">
          <Table
            title={() => 'Waiting Tickets'}
            dataSource={this.state.waitingTickets}
            columns={this.ticketsColumns.filter(
              c => c.title !== 'Agent Identity'
            )}
            loading={this.state.isLoading}
          />
        </Card>

        <Card className="mt2">
          <Table
            title={() => 'Open Tickets'}
            dataSource={this.state.openTickets}
            columns={this.ticketsColumns.filter(c => c.title !== 'Queue Time')}
            loading={this.state.isLoading}
          />
        </Card>
      </div>
    )
  }
}